<template>
  <Modal ref="modalRef" width="30rem">
    <template #header>Add collaborators</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <Hint icon="info-circle">
          <template v-if="subject.kind === 'building'">
            Everyone you pick can work on this building like its manager.
          </template>
          <template v-else>
            Everyone you pick can run this unit like its manager and sees it on their home page.
          </template>
          They're offered as showing agents for it too. Notifications stay with the manager, and Tallo doesn't message
          them about the change.
        </Hint>

        <Input
          v-if="candidates.length >= searchFrom"
          v-model="search"
          type="search"
          placeholder="Search your team"
          aria-label="Search your team"
        />

        <View direction="column" gap="xs" role="group" aria-label="Your team">
          <!-- The whole row toggles; a <label> can't wrap it, since the avatar and name render labels of their own -->
          <div
            v-for="member in shown"
            :key="member.investorId"
            class="candidate"
            :class="{ checked: isChecked(member), disabled: isCollaborator(member) }"
            @click="onRowClick(member, $event)"
          >
            <Checkbox
              :id="checkboxId(member)"
              :model-value="isChecked(member)"
              :disabled="isCollaborator(member)"
              :aria-labelledby="`${checkboxId(member)}-name`"
              :aria-describedby="`${checkboxId(member)}-about`"
              @update:model-value="toggle(member, Boolean($event))"
            />
            <Avatar :user-name="nameOf(member)" show-single-letter size="small" />
            <View direction="column" class="identity">
              <Text
                :id="`${checkboxId(member)}-name`"
                as="span"
                variant="label-medium"
                :color="isCollaborator(member) ? 'tertiary' : 'primary'"
              >
                {{ member.isYou ? `${nameOf(member)} (you)` : nameOf(member) }}
              </Text>
              <Text
                :id="`${checkboxId(member)}-about`"
                as="span"
                variant="body-small"
                :color="isCollaborator(member) ? 'tertiary' : 'secondary'"
              >
                {{ subtitleOf(member) }}
              </Text>
            </View>
          </div>

          <Text v-if="!shown.length" variant="body-small" color="secondary">Nobody on your team matches that.</Text>
        </View>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isAdding" :disabled="!picked.length" @click="add">{{ addLabel }}</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Avatar, Button, Checkbox, Hint, Input, Modal, Text, View, useAlertsStore } from '@tallo/design-system';
import { CompanyTeamMemberDto, companyRoleLabels, teamMemberName as nameOf } from '@tallo/investor/company';
import { escapeHtml } from '@tallo/utility';

import { TeamSubject } from '../../dtos/property-team.dto.interface';
import { useAddPropertyCollaborator } from '../../mutations/property-team.mutation';

const props = withDefaults(
  defineProps<{
    subject: TeamSubject;
    members: CompanyTeamMemberDto[];
    managerId?: string | null;
    collaboratorIds: string[];
    /** What each seat already is on this unit, shown after their company role */
    hints?: Record<string, string>;
  }>(),
  { hints: () => ({}) },
);

// Below this many people the whole team fits on screen, and a search box is one more thing to read
const searchFrom = 6;

const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const picked = ref<string[]>([]);
const search = ref('');
const isAdding = ref(false);
const { mutateAsync } = useAddPropertyCollaborator();

// The manager already works the unit, so they are not offered; current collaborators stay listed, greyed out
const candidates = computed(() => props.members.filter((member) => member.investorId !== props.managerId));
const shown = computed(() => {
  const query = search.value.trim().toLowerCase();

  return query
    ? candidates.value.filter((member) => `${member.name} ${member.email}`.toLowerCase().includes(query))
    : candidates.value;
});
const addLabel = computed(() => {
  const count = picked.value.length;

  return count > 1 ? `Add ${count} collaborators` : 'Add collaborator';
});

function checkboxId(member: CompanyTeamMemberDto): string {
  return `add-collaborator-${member.investorId}`;
}

function isCollaborator(member: CompanyTeamMemberDto): boolean {
  return props.collaboratorIds.includes(member.investorId);
}

function isChecked(member: CompanyTeamMemberDto): boolean {
  return isCollaborator(member) || picked.value.includes(member.investorId);
}

function subtitleOf(member: CompanyTeamMemberDto): string {
  if (isCollaborator(member)) {
    return 'Already a collaborator';
  }

  return [companyRoleLabels[member.role], props.hints[member.investorId]].filter(Boolean).join(' · ');
}

function toggle(member: CompanyTeamMemberDto, checked: boolean) {
  picked.value = checked
    ? [...picked.value, member.investorId]
    : picked.value.filter((investorId) => investorId !== member.investorId);
}

/** A click anywhere on the row but the checkbox itself, which toggles on its own */
function onRowClick(member: CompanyTeamMemberDto, event: MouseEvent) {
  if (isCollaborator(member) || (event.target as HTMLElement).closest('.checkbox')) {
    return;
  }

  toggle(member, !picked.value.includes(member.investorId));
}

function open() {
  picked.value = [];
  search.value = '';
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

/** One at a time, so a refusal stops the rest and leaves them picked for another try */
async function add() {
  const targets = candidates.value.filter((member) => picked.value.includes(member.investorId));
  const added: CompanyTeamMemberDto[] = [];

  isAdding.value = true;

  try {
    for (const target of targets) {
      await mutateAsync({ subject: props.subject, investorId: target.investorId });
      added.push(target);
    }
  } catch {
    // The mutation already showed the server's message; whoever got in is now shown as a collaborator
    picked.value = picked.value.filter((investorId) => !added.some((member) => member.investorId === investorId));

    return;
  } finally {
    isAdding.value = false;
  }

  close();
  alertsStore.success(
    added.length === 1
      ? `<b>${escapeHtml(nameOf(added[0]))}</b> added as a collaborator`
      : `${added.length} collaborators added`,
  );
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}

.candidate {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-sm);
  padding: var(--t-spacing-sm);
  border-radius: var(--t-border-radius-sm);
  transition: background-color 150ms var(--t-ease-in-out);

  cursor: pointer;

  &:hover:not(.disabled),
  &.checked:not(.disabled) {
    background-color: var(--t-color-neutral-50);
  }

  &.disabled {
    cursor: default;
  }
}

// A long name or email has nowhere to break, so it wraps rather than pushing the dialog past a phone's width
.identity {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
